import { create } from 'zustand';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
}

interface NotificationActions {
  // CRUD operations
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  
  // State management
  setNotifications: (notifications: Notification[]) => void;
  setLoading: (loading: boolean) => void;
  
  // Utility methods
  getNotificationsByType: (type: Notification['type']) => Notification[];
  getUnreadNotifications: () => Notification[];
  getNotificationById: (id: string) => Notification | undefined;
}

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  (set, get) => ({
    // Initial state
    notifications: [],
    unreadCount: 0,
    isLoading: false,

    // CRUD operations
    addNotification: (notificationData) => {
      const newNotification: Notification = {
        ...notificationData,
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        read: false
      };

      set((state) => ({
        notifications: [newNotification, ...state.notifications],
        unreadCount: state.unreadCount + 1
      }));

      // Auto-remove notification after 5 seconds (except for errors)
      if (notificationData.type !== 'error') {
        setTimeout(() => {
          get().removeNotification(newNotification.id);
        }, 5000);
      }
    },

    removeNotification: (id) => {
      set((state) => {
        const notification = state.notifications.find(n => n.id === id);
        const wasUnread = notification && !notification.read;
        
        return {
          notifications: state.notifications.filter(n => n.id !== id),
          unreadCount: wasUnread ? state.unreadCount - 1 : state.unreadCount
        };
      });
    },

    markAsRead: (id) => {
      set((state) => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification && !notification.read) {
          return {
            notifications: state.notifications.map(n =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: state.unreadCount - 1
          };
        }
        return state;
      });
    },

    markAllAsRead: () => {
      set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    },

    clearAll: () => {
      set({ notifications: [], unreadCount: 0 });
    },

    // State management
    setNotifications: (notifications) => {
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    },

    setLoading: (loading) => set({ isLoading: loading }),

    // Utility methods
    getNotificationsByType: (type) => {
      return get().notifications.filter(n => n.type === type);
    },

    getUnreadNotifications: () => {
      return get().notifications.filter(n => !n.read);
    },

    getNotificationById: (id) => {
      return get().notifications.find(n => n.id === id);
    }
  })
);


